<form
  [formGroup]="form"
  (ngSubmit)="onSubmitForm()">
  @if (isAddLoading() || isUpdateLoading()) {
    <bn-web-ui-loading />
  }
  <bn-web-ui-card
    class="mb-2"
    [isFullWidth]="true">
    <div class="row">
      <div class="col-md-6 col-xl-6 col-12">
        <bn-web-ui-input
          borderRadius="lg"
          formControlName="name"
          id="app-shield-waf-rule-name"
          itemStyle="primary"
          label="Name"
          placeholder="Condition name"
          size="md"
          [hideErrorMessages]="true"
          (paste)="onPaste($event, 'name')" />

        <!-- Name custom error -->
        @let nameControl = form.get('name');
        @if (nameControl?.invalid && nameControl?.touched && nameControl?.errors) {
          <bn-web-ui-formcontrol-errors [withCustomPlaceHolder]="true">
            Please enter a name for your WAF Rule. It can include letters, numbers and spaces only.
          </bn-web-ui-formcontrol-errors>
        }
      </div>

      <div class="col-md-6 col-xl-6 col-12">
        <bn-web-ui-input
          borderRadius="lg"
          formControlName="description"
          id="app-shield-waf-rule-description"
          itemStyle="primary"
          label="Description"
          placeholder="Enter WAF Rule description"
          size="md"
          [hideErrorMessages]="true"
          (paste)="onPaste($event, 'description')" />

        <!-- Description custom error  -->
        @let descriptionControl = form.get('description');
        @if (descriptionControl?.invalid && descriptionControl?.touched && descriptionControl?.errors) {
          <bn-web-ui-formcontrol-errors [withCustomPlaceHolder]="true">
            Please enter a description for your WAF Rule. It can include letters, numbers and spaces only.
          </bn-web-ui-formcontrol-errors>
        }
      </div>
    </div>
  </bn-web-ui-card>
  <bn-web-ui-card
    class="mb-2"
    heading="Request Matching">
    <bn-web-ui-card
      backgroundColor="dark-1"
      formArrayName="requestMatchers"
      [learnMore]="{
        href: 'https://docs.bunny.net/docs/shield-creating-a-custom-waf-rule',
        options: {
          topValue: 'var(--bn-spacer-3)',
          rightValue: 'var(--bn-spacer-3)',
        },
      }"
      [noPadding]="true">
      <!-- requestMatchers form  -->
      @for (matcher of requestMatchers.controls; track matcher; let isFirst = $first; let isLast = $last) {
        <div
          class="px-4 pt-2 pb-0"
          [formGroupName]="$index"
          [ngClass]="{ 'pt-4': isFirst, 'pb-4': isLast }">
          @if (isFirst) {
            <p class="base-2">Please use the below fields to build a request criteria to match against.</p>
          }
          <div class="d-flex flex-row gap-1 align-items-start">
            <div class="flex-fill">
              <bn-web-ui-card
                data-e2e="WAFRulesCondition"
                size="sm"
                [noPadding]="true">
                <div
                  class="row"
                  style="--bs-gutter-y: var(--bn-spacer-3)">
                  <div class="col-12 col-xl-6">
                    <bn-web-ui-select
                      formControlName="ConditionsVariable"
                      id="app-shield-waf-variable-type"
                      label="Variable"
                      size="md"
                      [items]="wafRuleVariableTypes | mapWafEnumToSelectItems: isPremium"
                      [options]="{
                        isSearchable: true,
                        placeholder: 'Select variable type',
                      }" />
                    @if (matcher.get('VariableValue')?.disabled === false) {
                      <div class="mt-2">
                        @let variableSupportedValues = getSupportedValues(matcher.get('ConditionsVariable')?.value);
                        @if (variableSupportedValues) {
                          <bn-web-ui-select
                            formControlName="VariableValue"
                            id="app-shield-waf-variable-value"
                            label="Variable Value"
                            size="md"
                            [items]="variableSupportedValues.values | mapWafValuesToSelectItems: matcher.get('VariableValue')?.value"
                            [options]="{
                              addTag: variableSupportedValues.editable === true,
                              addTagText: 'Use',
                              clearable: true,
                              hideClear: false,
                              isSearchable: true,
                              placeholder: variableSupportedValues.editable === true ? 'Select or enter value' : 'Select value',
                            }" />
                        } @else {
                          <bn-web-ui-input
                            formControlName="VariableValue"
                            label="Variable Value"
                            placeholder="Enter value"
                            size="md" />
                        }
                      </div>
                    }
                  </div>
                  <!-- RIGHT column: Operator + its value -->
                  <div class="col-12 col-xl-6">
                    <bn-web-ui-select
                      formControlName="ConditionsOperator"
                      id="app-shield-waf-operator-type"
                      label="Operator"
                      size="md"
                      [items]="wafRuleOperatorType | mapWafEnumToSelectItems: isPremium"
                      [options]="{
                        isSearchable: true,
                        placeholder: 'Select operator type',
                      }" />

                    @if (matcher.get('OperatorValue')?.disabled === false) {
                      <div class="mt-2">
                        @let recommendedOperator = getRecommendedOperator(matcher.get('ConditionsVariable')?.value);
                        @if (recommendedOperator) {
                          <bn-web-ui-select
                            formControlName="OperatorValue"
                            id="app-shield-waf-operator-value"
                            label="Operator Value"
                            size="md"
                            [items]="recommendedOperator.values | mapWafValuesToSelectItems: matcher.get('OperatorValue')?.value"
                            [options]="{
                              addTag: true,
                              addTagText: 'Use',
                              clearable: true,
                              hideClear: false,
                              isSearchable: true,
                              placeholder: 'Select or enter value',
                            }" />
                        } @else {
                          <bn-web-ui-input
                            formControlName="OperatorValue"
                            label="Operator Value"
                            placeholder="Enter value"
                            size="md" />
                        }
                      </div>
                    }
                  </div>
                </div>
              </bn-web-ui-card>
            </div>
            @if (requestMatchers.controls.length > 1) {
              <div class="flex-shrink-1 ms-1">
                <!-- Remove button -->
                <bn-web-ui-button
                  aria-label="Remove condition"
                  class="bn-button--circle bn-button--remove"
                  itemStyle="stroke"
                  size="xs"
                  title="Remove condition"
                  type="button"
                  (click)="onRemoveMatcher($index)">
                  <fa-icon icon="xmark" />
                </bn-web-ui-button>
              </div>
            }
          </div>
        </div>
      }
      <!-- ADD MATCHER -->
      <div class="ms-1 pb-2">
        <button
          bn-web-ui-button
          itemStyle="link"
          type="button"
          [disabled]="!requestMatchers.valid"
          [tooltip]="!requestMatchers.valid ? 'Fill required fields to add more' : undefined"
          (click)="onAddMatcher()">
          + Add Condition
        </button>
      </div>
    </bn-web-ui-card>
    <!-- Transformer -->
    <div class="py-4">
      <bn-web-ui-select
        formControlName="transformations"
        label="Transformations"
        size="md"
        [items]="wafRuleTransformationType | mapWafEnumToSelectItems: isPremium"
        [options]="options" />
    </div>
  </bn-web-ui-card>
  <bn-web-ui-card
    class="mb-2"
    heading="Response action">
    <!-- Actions -->
    <bn-web-ui-card
      backgroundColor="dark-1"
      [noPadding]="true">
      <div class="p-4">
        <p class="base-2">Please use the below fields to set the outcome of a triggered WAF rule.</p>
        <bn-web-ui-card
          class="bn-app-shield-waf-custom-rule-add-page__card-border flex-sm-grow-1 mb-2"
          size="sm">
          <bn-web-ui-select
            formControlName="actionType"
            id="app-shield-rate-limit-action"
            size="md"
            [items]="wafRuleActionType | mapWafEnumToSelectItems: isPremium"
            [options]="{
              isSearchable: true,
              placeholder: 'Select action',
            }" />
        </bn-web-ui-card>
      </div>
    </bn-web-ui-card>
  </bn-web-ui-card>

  <!-- Skeletons -->
  <ng-template #loading>
    <ngx-skeleton-loader
      appearance="line"
      count="1"
      [theme]="{
        'height.px': 18,
        'margin-bottom': 0,
        'width.px': 100,
        overflow: 'hidden',
      }" />
  </ng-template>
  <div
    bnIsElementStuck
    class="mt-2"
    [stickyEnabled]="form.dirty">
    <div class="bn-sticky-save-button__wrapper d-flex align-items-center justify-content-between">
      <button
        bn-web-ui-button
        size="xl"
        type="submit"
        [loading]="isAddFinished() || isUpdateFinished()">
        {{ wafRuleId ? 'Update Custom WAF Rule' : 'Save' }}
      </button>
      <button
        bn-web-ui-button
        class="ms-3"
        itemStyle="stroke"
        size="xl"
        type="button"
        (click)="onCancelClick()">
        Cancel
      </button>
      @if (form.dirty) {
        <span class="bn-container-list__save-row__unsaved-title"
          ><fa-icon
            class="ms-4"
            icon="triangle-exclamation" />
          You have unsaved changes.</span
        >
      }
    </div>
  </div>
</form>
